import 'banner-pattern/dist/main.css';
import { useEffect, useState } from 'react';
import TimeOffBalance from '../TimeOffBalance'
import { EmployeeInfo } from '../EmployeeInfo'
import { useDispatch, useSelector } from 'react-redux';
import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs';
import Table from '../../../components/common/Table/Table';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Datepicker from '../../../components/common/DatePicker/Datepicker';
import { LabeledValue } from '../../../components/common/LabeledValue';
import Modal from '../../../components/common/Modal/Modal';
// ValidationBanner intentionally unused in this view
import { AppDispatch, RootState } from '../../../store/store';
import { useProfileDisplay } from '../../../hooks/useProfileDisplay';
import { formatDateSeries } from '../../../utils/dataTransformSeries';
import { convertTo12HourFormat } from '../../../utils/timeStringParser';
import { formatHours } from '../../../utils/formatters';
import useWithdrawRequest from '../../../hooks/useWithdrawRequest';
import WithdrawnView from '../../../pages/Requestor/StatusViews/detail/WithdrawnView';
import type { TimeOffRequestDetails } from '../../../store/slices/Requestor/timeOffDetailsSlice';
import Loader from '../../../components/common/Loader';
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner';
import { getDisplayedOtherHoursTotal } from '../StatusViews/detail/constants';
import { useOtherRequestDetails } from '../StatusViews/detail/useOtherRequestDetails';
import { formatDateTimeWithTimezone, convertMMDDYYYYToYYYYMMDD } from '../../../utils/date';
import { fetchProfile } from '../../../store/slices/profileSlice';

const stripParentheses = (value: string): string => value.replaceAll(/[()]/g, '').trim()

const formatActorDisplay = (name: string, nuid: string): string => {
    const trimmedName = name.trim()
    const trimmedNuid = stripParentheses(nuid)

    if (trimmedName && trimmedNuid) return `${trimmedName} (${trimmedNuid})`
    return trimmedName || trimmedNuid
}


const RequestDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>()
    const location = useLocation();
    const { mode } = useParams<{ mode?: string }>()
    const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
    const [showWithdrawSuccess, setShowWithdrawSuccess] = useState(false);
    const { withdraw, loading: withdrawLoading, error: withdrawError } = useWithdrawRequest();

    // Determine mode from URL parameter
    const isAdminMode = mode === 'admin';

    // Get employee data from location state (for admin mode)
    const locationState = location.state as {
      employeeId?: string;
      employeeName?: string;
      selectedDates?: string[];
      timeEntries?: any[];
    } | null;

    const employeeId = locationState?.employeeId;
    const employeeName = locationState?.employeeName;

    // Extract dynamic data from navigation state with fallback to session storage
    const stateData = location.state || {};
    // Get Time Off Request ID from state
    const timeOffRequestId = stateData.timeOffRequestId || '';
    
    // Get user info - try state first, then session storage
    // userId is the employee ID (e.g., X000233)
    // Prefer selected manual employee from redux when available (admin/manual mode)
    const manualSelected = useSelector((s: RootState) => s.manualTimeOff.selectedEmployee)

    // Start with navigation state (if present)
    let manualUserNuid = stateData.userNuid || ''
    let manualUserName = stateData.userName || ''

    // Prefer manual selection from Redux when present (admin/manual mode)
    if (manualSelected?.nuid) {
        manualUserNuid = manualSelected.nuid
        manualUserName = manualSelected.name || manualUserName
    }

    const userNuid = manualUserNuid
    const userName = manualUserName
    
    // Get logged-in user profile (for both employee and proxy display)
    const {
        userInfo: loggedInUserName,
        userNuid: loggedInUserNuid,
        employee: loggedInEmployee,
    } = useProfileDisplay()

    const loggedInActorName =
        loggedInUserName ||
        [
            loggedInEmployee?.empFirstName,
            loggedInEmployee?.empMiddleName,
            loggedInEmployee?.empLastName,
        ]
            .filter(Boolean)
            .join(' ') ||
        (stateData.userName === 'Employee' ? '' : stateData.userName) ||
        sessionStorage.getItem('userName') ||
        ''
    const loggedInActorNuid =
        loggedInUserNuid ||
        loggedInEmployee?.empNuid ||
        stateData.userNuid ||
        sessionStorage.getItem('userId') ||
        sessionStorage.getItem('nuid') ||
        ''
    const loggedInActorDisplay = formatActorDisplay(loggedInActorName, loggedInActorNuid)

    useEffect(() => {
        if (!loggedInActorName && loggedInActorNuid) {
            dispatch(fetchProfile())
        }
    }, [dispatch, loggedInActorName, loggedInActorNuid])
    
    // For "Time Off Request For" - use appropriate employee info
    const displayEmployeeName = isAdminMode ? userName : loggedInUserName
    const displayEmployeeNuid = isAdminMode ? userNuid : loggedInUserNuid.replaceAll(/[()]/g, '').trim()
    
    const getSubmittedByInfo = () => {
      if (isAdminMode) {
                return loggedInActorDisplay ? `Requestor Proxy ${loggedInActorDisplay}` : ''
      }
      // Show logged-in employee name with NUID
            return loggedInActorDisplay
    };
    
// Get submission timestamp and comments
    const submittedOn = formatDateTimeWithTimezone(stateData.submittedOn) || '';
    const comments = stateData.comments || '';

    // Extract initial dates from incoming state
    const incomingDates = (location.state)?.selectedDates;
    
    // Table columns and sample rows for the Time entries table
    const tableColumns = [
        { key: 'requestType', label: 'Request Type' },
        { key: 'date', label: 'Date' },
        { key: 'day', label: 'Day' },
        { key: 'timesRequested', label: 'Time Requested' },
        { key: 'hours', label: 'Hours' },
        { key: 'hoursOtherRequests', label: 'Hours in Other Request' },
    ];

    // Use timeEntries provided via navigation state when available (submitted payload)
    const incomingTimeEntries = (location.state)?.timeEntries;
    const baseTableRows = Array.isArray(incomingTimeEntries) && incomingTimeEntries.length > 0 ?
        incomingTimeEntries.map((entry: any, idx: number) => ({ 
          id: `r${idx + 1}`, 
          ...entry,
          // Convert times to 12-hour AM/PM format for partial days
          timesRequested: convertTo12HourFormat(entry.timesRequested || '')
        })) :
        [];

    // Extract dates from baseTableRows as fallback
    const fallbackDates = baseTableRows.map(row => row.date);
    const rawRequestedDates = Array.isArray(incomingDates) && incomingDates.length > 0 ? incomingDates : fallbackDates;

    // Keep dates in MM/DD/YYYY for the Datepicker and useOtherRequestDetails.
    // Normalise any stray YYYY-MM-DD entries (e.g. preserved drafts) to the
    // canonical display format so all consumers receive a consistent format.
    const requestedDates = rawRequestedDates
      .map(date => {
        if (!date) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          const [yyyy, mm, dd] = date.split('-');
          return `${mm}/${dd}/${yyyy}`;
        }
        return date;
      })
      .filter(Boolean);

    // ISO (YYYY-MM-DD) version used only by formatDateSeries
    const requestedDatesISO = requestedDates.map(d => convertMMDDYYYYToYYYYMMDD(d));

    // Fetch other request details for the calendar (month/year tracking, fetch,
    // and date/tooltip transforms) via the shared hook — same logic used by
    // DraftView.tsx and the other status views, rather than a duplicated copy.
    const {
        otherRequestDates,
        otherRequestDetailsForTooltip,
        handleMonthYearChange,
    } = useOtherRequestDetails(requestedDates, false, timeOffRequestId || undefined)

    const handleBreadcrumbPrevious = () => {
        // Build path dynamically based on mode parameter
        const basePath = mode ? `/${mode}/requestor` : '/requestor'
        navigate(basePath, {
            state: isAdminMode
                ? {
                      employeeId,
                      employeeName,
                  }
                : undefined,
        });
    };

    // compute totals and append as final row
    const totalHours = baseTableRows.reduce((sum, r) => sum + (Number.parseFloat(String(r.hours)) || 0), 0);
    const totalOther = baseTableRows.reduce((sum, r) => sum + getDisplayedOtherHoursTotal(String(r.hoursOtherRequests || '')), 0);

    const tableRows = [
        ...baseTableRows,
        {
            id: 'totals-row',
            requestType: 'Total Hours',
            date: '',
            day: '',
            timesRequested: '',
            hours: formatHours(totalHours),
            hoursOtherRequests: formatHours(totalOther),
        },
    ];

    const withdrawnTableRows = tableRows.map((row) => ({
        ...row,
        times: row.timesRequested,
        hoursInOtherRequests: row.hoursOtherRequests,
    }));

    const withdrawnRequestDetails: TimeOffRequestDetails = {
        statuses: [],
        auditHistory: [],
        requestId: timeOffRequestId,
        todate: requestedDatesISO.at(-1) || '',
        hoursRequested: formatHours(totalHours),
        timeOffReqstType: 'Time Off Request',
        status: 'Withdrawn',
        detail: '',
        employeeName: displayEmployeeName,
        nuid: displayEmployeeNuid,
        submittedOn,
        submittedBy: getSubmittedByInfo(),
        employeeComments: comments,
        // Set directly (rather than relying on WithdrawnView's fallback) since this
        // confirmation page has no statuses/auditHistory for it to derive these from.
        // Use the un-prefixed actor identity — WithdrawnView itself prepends
        // "Requestor Proxy " when manualReqRcvdDate is set, so prefixing here too
        // would double it up.
        withdrawnOn: formatDateTimeWithTimezone(new Date().toISOString()),
        withdrawnBy: loggedInActorDisplay,
        manualReqRcvdDate: location.state?.adminReceivedDate,
        employee: {
            nuid: displayEmployeeNuid,
            firstName: displayEmployeeName,
            middleName: null,
            lastName: '',
            fullName: displayEmployeeName,
        },
        requestedDates: baseTableRows.map((row) => ({
            requestType: row.requestType || 'Time Off',
            date: convertMMDDYYYYToYYYYMMDD(row.date),
            day: row.day,
            times: row.timesRequested,
            requestedHours: row.hours,
            hoursInOtherRequests: row.hoursOtherRequests || 0,
        })),
    };
    
    if (showWithdrawSuccess) {
        return (
            <div className='page-container'>
                <BreadCrumbs
                    previousPage="TORT"
                    currentPage="Create New Time Off Request"
                    onPreviousClick={handleBreadcrumbPrevious}
                />
               {isAdminMode && <div className='mt-6!'><ManualTimeOffBanner/></div>}
                <WithdrawnView
                    data={withdrawnRequestDetails}
                    tableRows={withdrawnTableRows}
                    highlightedDates={requestedDates}
                    showSuccessBanner
                />
                <TimeOffBalance />
                <EmployeeInfo />
            </div>
        );
    }

    return (
        <div className='page-container'>
            <BreadCrumbs
                previousPage="TORT"
                currentPage="Create New Time Off Request"
                onPreviousClick={handleBreadcrumbPrevious}
            />
            <div className='flex flex-col md:flex-row gap-3 items-stretch mt-8! w-full'>
                {isAdminMode ? (
                    <ManualTimeOffBanner isSuccessPage={true} />
                ) : (
                    <div
                        className="ds-banner ds-banner__bg--white ds-banner--icon p-3 mb-0 w-full md:w-1/2 flex flex-row items-start gap-3"
                        data-ds-variant="basic"
                        data-ds-version="1"
                        data-ds-theme="vessel"
                        aria-labelledby="success-banner-title"
                        style={{ borderColor: "#003B71" }}
                    >
                        <div className="ds-banner__icon-img shrink-0">
                            <img
                                src="/assets/images/informationIcon.svg"
                                alt="info icon"
                                style={{ width: "42px", height: "42px" }}
                            />
                        </div>
                        <div className="ds-banner__container flex-1">
                            <div className="ds-banner__main flex flex-col gap-0">
                                <div
                                    className="ds-banner__main-header--awareness"
                                    id="success-banner-title"
                                    style={{ fontWeight: 350, fontSize: "20px" }}
                                >
                                     You have successfully submitted your time off request.
                                </div>
                                <div className="ds-banner__main-body text-sm leading-tight">
                                    This time off request was submitted and is pending review by your approver.
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className='p-6! rounded-lg! mb-0 w-full md:w-1/2 bg-[#EBF4FA]'>
                    <LabeledValue label="Time Off Request For" value={`${displayEmployeeName} (${displayEmployeeNuid})`} />
                    {requestedDates && requestedDates.length > 0 && (
                      <div className="mt-2">
                        <LabeledValue label="Time Off Request Dates" value={formatDateSeries(requestedDatesISO)} />
                      </div>
                    )}
                </div>
            </div>
            <div>
                <LabeledValue label="Time Off Request ID" value={timeOffRequestId} />
                <LabeledValue label="Submitted On" value={submittedOn} />
                <LabeledValue label="Submitted By" value={getSubmittedByInfo()} />
                {isAdminMode && <LabeledValue label={"Date Manual Request Received"} value={location.state.adminReceivedDate}/>}
                <p className='p3 -medium'>Employee Comments:</p>
                {/* Only render the paragraph when the user actually entered a comment */}
                {comments && <p className='p3 -book'>{comments}</p>}
            </div>
            <button type="button" className='button -light' onClick={() => setIsWithdrawOpen(true)}>Withdraw Request</button>
            <hr className='-variation-1' />
            {/* Time entries table */}
            <div className="mt-8!">
                <Table columns={tableColumns} rows={tableRows} showPagination={false} caption='Current Request' />
            </div>
            <p className='p3 -book'>View request details by clicking a specific date in the request.</p>
            
            {/* Calendar with pre-populated leaves from API */}
            <div className='mt-12! relative'>
                {/* Get all existing leaves from otherReqDetails API data */}
                {(() => {
                    return (
                        <Datepicker
                            name="requestDates"
                            openBehaviour="calendarView"
                            value={requestedDates}
                            datesToBeHighlighted={requestedDates}
                            onMonthYearChange={handleMonthYearChange}
                            otherSelectedDatesToShow={otherRequestDates}
                            otherRequestDetails={otherRequestDetailsForTooltip}
                            readOnly={true}
                            calendarWidth={350}
                            calendarHeight={350}
                        />
                    )
                })()}
            </div>
            {withdrawLoading && <Loader />}
            {isWithdrawOpen && (
                <>
                    <style>{`#modal-manual-trig .modal-save-btn{display:none !important;}`}</style>
                    <Modal
                        isOpen={isWithdrawOpen}
                        title="Withdraw Request"
                        onClose={() => setIsWithdrawOpen(false)}
                        loading={withdrawLoading}
                        submitBtn="Withdraw Request"
                        backBtn="Cancel"
                        onPrimary={async () => {
                            if (!timeOffRequestId || !userNuid) {
                                return
                            }
                            const success = await withdraw(timeOffRequestId, userNuid)
                            if (success) {
                                setIsWithdrawOpen(false)
                                setShowWithdrawSuccess(true)
                            }
                        }}
                        onBack={() => setIsWithdrawOpen(false)}
                    >
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <img className="w-10 h-10 shrink-0" src="/assets/images/alertSolid.svg" alt="warning" />
                            <div>
                                <p className="p2 -semibold">You are requesting to withdraw this time off request. It will be removed from the approvers queue.</p>
                                {withdrawError && <p className="p3 -book text-red-600 mt-2">{withdrawError}</p>}                            </div>
                        </div>
                    </Modal>
                </>
            )}
            <TimeOffBalance />
            <EmployeeInfo />
        </div>
    );
};

export default RequestDetails
